import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Avatar from '../Avatar';
import PetBadge from '../PetBadge';
import PetFormModal from '../PetFormModal';
import { deletePet } from '../../services/pets';
import { nextBirthday, countdownLabel } from '../../utils/dates';

function errText(err, fallback) {
    return err.response?.data?.error || fallback;
}

/**
 * PetsTab — the pets belonging to one group. Any member can add a pet (and
 * becomes its caretaker); only the caretaker sees Edit/Delete. Tapping a pet
 * opens its profile (/pet/:id) where its wish list lives.
 */
export default function PetsTab({ group, pets, user, onChanged, notify }) {
    const navigate = useNavigate();
    const [modal, setModal] = useState(null); // { pet } for edit, {} for create

    const handleDelete = async (e, pet) => {
        e.stopPropagation();
        if (!window.confirm(
            `Delete "${pet.name}"? All of their wishes go too — including any gifts other members have already claimed or bought. This can't be undone.`
        )) return;
        try {
            await deletePet(pet.id);
            notify('success', `${pet.name} was removed.`);
            onChanged();
        } catch (err) {
            notify('error', errText(err, 'Could not delete the pet.'));
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Pets in {group.name}</h2>
                <button
                    onClick={() => setModal({})}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                >
                    + Add a pet
                </button>
            </div>

            {pets.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg shadow text-gray-500">
                    No pets here yet. Add one to start a wish list for them.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {pets.map((pet) => {
                        const isCaretaker = pet.owner_user_id === user?.id;
                        const bday = nextBirthday(pet.birthday);
                        const gotcha = nextBirthday(pet.gotcha_day); // next occurrence of any month/day
                        return (
                            <div
                                key={pet.id}
                                onClick={() => navigate(`/pet/${pet.id}`)}
                                className="bg-white rounded-lg shadow p-4 hover:shadow-lg transition-shadow cursor-pointer flex flex-col"
                            >
                                <div className="flex items-center gap-3">
                                    <Avatar name={pet.name} url={pet.avatar_url} color={pet.avatar_color} seed={pet.id} size={44} />
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-semibold text-gray-900 truncate">{pet.name}</h3>
                                            <PetBadge species={pet.species} />
                                        </div>
                                        <p className="text-xs text-gray-500">
                                            Cared for by {isCaretaker ? 'you' : pet.owner?.name || 'a member'}
                                        </p>
                                    </div>
                                </div>

                                {(bday || gotcha) && (
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {bday && (
                                            <span className="text-xs px-2 py-0.5 rounded bg-pink-100 text-pink-700">
                                                🎂 {bday.label} · {countdownLabel(bday.daysUntil)}
                                            </span>
                                        )}
                                        {gotcha && (
                                            <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">
                                                🏡 {gotcha.label}
                                            </span>
                                        )}
                                    </div>
                                )}

                                <div className="mt-auto pt-3 flex items-center justify-between">
                                    <span className="text-sm text-blue-600">View wishes →</span>
                                    {isCaretaker && (
                                        <div className="flex items-center gap-3">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setModal({ pet }); }}
                                                className="text-sm text-gray-600 hover:text-gray-900"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={(e) => handleDelete(e, pet)}
                                                className="text-sm text-red-600 hover:text-red-700"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {modal && (
                <PetFormModal
                    pet={modal.pet}
                    groupId={group.id}
                    onClose={() => setModal(null)}
                    onSaved={(saved, isEdit) => {
                        setModal(null);
                        notify('success', isEdit ? 'Pet updated.' : `${saved.name} added.`);
                        onChanged();
                    }}
                />
            )}
        </div>
    );
}
